/**
 * Notches preview - equity options signals page.
 *
 * Lives at /notches/dashboard/signals/options/equity. This product (single-
 * stock options signals on liquid US equities) is already shipping via the
 * legacy /trade-signals page; this preview surface scaffolds the breadcrumb /
 * entitlement state then deeplinks into the legacy page for the actual
 * content. The page rebuild happens in a later chunk (per-name tabs, sortable
 * candidate table, etc.) without touching the legacy /trade-signals surface.
 */
import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import { Navigate, Link, useNavigate } from 'react-router-dom';
import { hasSubscription } from '../../lib/entitlements';
import { ENTITLEMENTS } from '../../constants/entitlements';

const c = {
  bg: '#0f172a', card: '#1f2937', cardAlt: '#1a2332', border: '#374151',
  text: '#f9fafb', muted: '#9ca3af', dim: '#6b7280', green: '#10b981',
  purple: '#8b5cf6',
};

export const EquityOptionsPreview: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (!hasSubscription(user, ENTITLEMENTS.NOTCHES_PREVIEW)) {
    return <Navigate to="/notches" replace />;
  }
  const subscribed = hasSubscription(user, ENTITLEMENTS.SIGNALS_OPTIONS_EQUITY);

  return (
    <div className="dashboard-container" style={{ background: c.bg, minHeight: '100vh', color: c.text }}>
      <Navigation />
      <div style={{ padding: '24px 32px', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ fontSize: 12, marginBottom: 10 }}>
          <Link to="/notches/dashboard" style={{ color: c.muted, textDecoration: 'none' }}>Notches</Link>
          <span style={{ color: c.dim, margin: '0 6px' }}>/</span>
          <Link to="/notches/dashboard/signals" style={{ color: c.muted, textDecoration: 'none' }}>Signals</Link>
          <span style={{ color: c.dim, margin: '0 6px' }}>/</span>
          <span style={{ color: c.text }}>Equity Options</span>
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          marginBottom: 6, paddingBottom: 10,
          borderBottom: `2px solid ${c.green}`,
        }}>
          <span style={{ width: 8, height: 28, borderRadius: 2, background: c.green }} />
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700 }}>Equity Options Signals</h1>
        </div>

        <p style={{ color: c.muted, fontSize: 14, marginTop: 16, marginBottom: 24 }}>
          Single-stock options signals on liquid US equities. Same six-component scoring model
          as Index + Futures Options.
        </p>

        {subscribed ? (
          <div style={{
            background: c.card, border: `1px solid ${c.border}`, borderRadius: 8, padding: 20,
          }}>
            <h2 style={{ margin: '0 0 8px', fontSize: 16 }}>Active in your plan</h2>
            <p style={{ color: c.muted, fontSize: 13, marginTop: 0, marginBottom: 16 }}>
              The preview rebuild of this page is in progress. For now, the legacy equity options
              signals page is fully featured.
            </p>
            <button
              onClick={() => navigate('/options-equity-signals')}
              style={{
                background: c.green, color: c.text, border: 'none',
                padding: '8px 16px', borderRadius: 4, fontSize: 13, fontWeight: 600,
                cursor: 'pointer',
              }}
            >Open Equity Options Signals (legacy view) &rarr;</button>
          </div>
        ) : (
          <div style={{
            background: c.card, border: `1px dashed ${c.border}`, borderRadius: 8, padding: 20,
          }}>
            <h2 style={{ margin: '0 0 8px', fontSize: 16, color: c.muted }}>Not in your plan</h2>
            <p style={{ color: c.muted, fontSize: 13, marginTop: 0, marginBottom: 16 }}>
              Equity options signals are a separate product. Add them to your plan for daily picks
              on liquid US equities.
            </p>
            <Link to="/upgrade" style={{
              display: 'inline-block', background: 'transparent', color: c.green,
              border: `1px solid ${c.green}`, padding: '6px 14px', borderRadius: 4,
              fontSize: 13, fontWeight: 600, textDecoration: 'none',
            }}>Add to your plan</Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default EquityOptionsPreview;
